import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";

import type {
  AdminRole,
  ProjectOption,
  UserDetail,
  UserListResponse,
  UserOverview,
  UserProjectMembership,
} from "@/lib/admin-users/types";

/** The user list's filter and paging state, as sent to the API. */
export interface UserListParams {
  search: string;
  view: UserListView;
  roleId: string;
  sort: UserListSort;
  order: "asc" | "desc";
}

export type UserListSort = "name" | "lastLogin" | "createdAt";

/** Rows fetched per "Afficher plus". */
export const USER_LIST_PAGE_SIZE = 50;

/** Saved views over the list: each is a filter the API understands. */
export type UserListView = "all" | "active" | "inactive" | "admins" | "never" | "dormant";

/** The error message our API envelope carries, else the fallback. */
async function failure(res: Response, fallback: string): Promise<Error> {
  // SAFETY: error bodies come from our own defineRoute envelope
  // ({ success: false, message }); null when the body is not JSON.
  const body = (await res.json().catch(() => null)) as { message?: string } | null;

  return new Error(body?.message ?? fallback);
}

export function userListSearchParams(params: UserListParams, page: number): URLSearchParams {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(USER_LIST_PAGE_SIZE),
    sortBy: params.sort,
    sortOrder: params.order,
  });

  if (params.search) query.set("search", params.search);

  if (params.roleId !== "all") query.set("roleId", params.roleId);

  switch (params.view) {
    case "active":
      query.set("status", "ACTIF");
      break;
    case "inactive":
      query.set("status", "INACTIF");
      break;
    case "admins":
      query.set("roleId", "1");
      break;
    case "never":
      query.set("hasLoggedIn", "false");
      break;
    case "dormant":
      query.set("dormant", "true");
      break;
    case "all":
      break;
  }

  return query;
}

export function adminUsersQueryOptions(params: UserListParams) {
  return infiniteQueryOptions({
    queryKey: ["admin", "users", "list", params],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<UserListResponse> => {
      const res = await fetch(`/api/admin/users?${userListSearchParams(params, pageParam)}`, {
        cache: "no-store",
      });

      if (!res.ok) throw await failure(res, "Impossible de charger les utilisateurs.");

      // SAFETY: GET /api/admin/users answers the UserListResponse envelope.
      return (await res.json()) as UserListResponse;
    },
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.pageCount ? last.pagination.page + 1 : undefined,
    // Filtering keeps the last rows on screen until the new ones land.
    placeholderData: keepPreviousData,
  });
}

export function adminUserQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ["admin", "users", "detail", userId],
    queryFn: async (): Promise<UserDetail> => {
      const res = await fetch(`/api/admin/users/${userId}`, { cache: "no-store" });

      if (!res.ok) throw await failure(res, "Impossible de charger cet utilisateur.");

      // SAFETY: GET /api/admin/users/$id answers the UserDetail shape.
      return (await res.json()) as UserDetail;
    },
    retry: false,
  });
}

export function adminUserOverviewQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ["admin", "users", "overview", userId],
    queryFn: async (): Promise<UserOverview> => {
      const res = await fetch(`/api/admin/users/${userId}/overview`, { cache: "no-store" });

      if (!res.ok) throw await failure(res, "Impossible de charger l'aperçu.");

      // SAFETY: GET /api/admin/users/$id/overview answers the UserOverview shape.
      return (await res.json()) as UserOverview;
    },
  });
}

export function adminUserProjectsQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ["admin", "users", "projects", userId],
    queryFn: async (): Promise<UserProjectMembership[]> => {
      const memberships: UserProjectMembership[] = [];
      let page = 1;

      while (true) {
        const res = await fetch(`/api/admin/users/${userId}/projects?page=${page}&pageSize=100`, {
          cache: "no-store",
        });

        if (!res.ok) throw await failure(res, "Impossible de charger les projets.");

        // SAFETY: the route answers { projects: { items } } with nullable joins;
        // rows whose project no longer exists are dropped below.
        const body = (await res.json()) as {
          projects: {
            pagination: { pageCount: number };
            items: Array<{
              id: string | null;
              name: string | null;
              projectNumber: string | null;
              isActive: boolean;
              endDate: string | null;
              psuClosedAt: string | null;
              memberCount: number;
              managerCount: number;
              role: string | null;
              joinedAt: string | null;
            }>;
          };
        };

        memberships.push(
          ...body.projects.items.flatMap((item) =>
            item.id && item.name
              ? [
                  {
                    id: item.id,
                    name: item.name,
                    projectNumber: item.projectNumber,
                    isActive: item.isActive,
                    endDate: item.endDate,
                    psuClosedAt: item.psuClosedAt,
                    memberCount: item.memberCount,
                    managerCount: item.managerCount,
                    // SAFETY: project_members.role is written only through the
                    // MEMBER/SUBMANAGER/MANAGER schema of the members route.
                    role: (item.role ?? "MEMBER") as UserProjectMembership["role"],
                    joinedAt: item.joinedAt,
                  },
                ]
              : [],
          ),
        );

        if (page >= body.projects.pagination.pageCount) break;
        page++;
      }

      return memberships;
    },
  });
}

/** Every project, for the "add to a project" picker. Shared with the other admin dropdowns. */
export const adminProjectOptionsQueryOptions = queryOptions({
  queryKey: ["admin", "projects", "options"],
  queryFn: async (): Promise<ProjectOption[]> => {
    const res = await fetch("/api/admin/projects", { cache: "no-store" });

    if (!res.ok) throw await failure(res, "Impossible de charger les projets.");
    // SAFETY: /api/admin/projects answers the array, or a { data } wrapper.
    const body = (await res.json()) as ProjectOption[] | { data: ProjectOption[] };

    return Array.isArray(body) ? body : (body.data ?? []);
  },
  staleTime: 60_000,
});

export const adminRolesQueryOptions = queryOptions({
  queryKey: ["admin", "roles"],
  queryFn: async (): Promise<AdminRole[]> => {
    const res = await fetch("/api/admin/roles", { cache: "no-store" });

    if (!res.ok) throw await failure(res, "Impossible de charger les rôles.");

    // SAFETY: GET /api/admin/roles answers the AdminRole[] contract.
    return (await res.json()) as AdminRole[];
  },
  staleTime: 5 * 60_000,
});

/** Active accounts for the manager picker: one page of 500 covers the company. */
export const adminUserOptionsQueryOptions = queryOptions({
  queryKey: ["admin", "users", "options"],
  queryFn: async (): Promise<
    Array<{ id: string; name: string; email: string; avatarUrl: string | null }>
  > => {
    const res = await fetch("/api/admin/users?limit=500&status=ACTIF&sortBy=name&sortOrder=asc", {
      cache: "no-store",
    });

    if (!res.ok) throw await failure(res, "Impossible de charger les utilisateurs.");
    // SAFETY: GET /api/admin/users answers the UserListResponse envelope.
    const body = (await res.json()) as UserListResponse;

    return body.data.map((row) => ({
      id: row.id,
      name: row.fullName ?? row.email,
      email: row.email,
      avatarUrl: row.avatarUrl,
    }));
  },
  staleTime: 60_000,
});

/** Complete reporting lines, shared by both scopes of the organization viewer. */
export const adminOrganizationQueryOptions = queryOptions({
  queryKey: ["admin", "users", "organization"],
  queryFn: async (): Promise<import("@/lib/admin-users/organization").OrganizationPerson[]> => {
    const res = await fetch("/api/admin/users/organization", { cache: "no-store" });

    if (!res.ok) throw await failure(res, "Impossible de charger l’organigramme.");

    // SAFETY: the admin-only organization route returns this minimal snapshot.
    return await res.json();
  },
});
